/**
 * Virtual OS Mouse Pointer & Cursor Animation Entity for ASAE Screencast & Application Automation.
 * Renders high-fidelity, razor-sharp vector cursors (Arrow, Hand, I-Beam, Crosshair) with an active
 * glowing contrast halo, animated click ripples, and floating Action / Keystroke HUD badges.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export type CursorType = 'arrow' | 'hand' | 'ibeam' | 'crosshair';

export interface PointerProps {
  cursorType?: CursorType;
  cursorColor?: string;
  borderColor?: string;
  size?: number;
  showTrail?: boolean;
  showHalo?: boolean;
  haloColor?: string;
  rippleColor?: string;
  isClicking?: boolean;
  clickProgress?: number;
  currentActionLabel?: string;
}

export class PointerObject extends SVGObject {
  public props: PointerProps;
  private cursorElement!: SVGElement;
  private haloElement!: SVGElement;
  private rippleElement!: SVGElement;
  private outerRippleElement!: SVGElement;
  private badgeElement!: SVGElement;
  private badgeTextElement!: SVGElement;

  constructor(config: SVGObjectConfig & { props?: PointerProps }) {
    super({ ...config, type: 'pointer' });
    this.props = {
      cursorType: 'arrow',
      cursorColor: '#ffffff',
      borderColor: '#000000',
      size: 38,
      showTrail: false,
      showHalo: true,
      haloColor: 'rgba(56, 189, 248, 0.28)',
      rippleColor: '#38bdf8',
      isClicking: false,
      clickProgress: 0,
      ...config.props
    };
  }

  public render(): void {
    this.container.innerHTML = '';
    const size = this.props.size || 38;

    // 1. Active Glowing Halo (guides student's eye smoothly across screen)
    if (this.props.showHalo !== false) {
      this.haloElement = Renderer.createElement('circle');
      Renderer.setAttributes(this.haloElement, {
        cx: 0,
        cy: 0,
        r: size * 0.75,
        fill: this.props.haloColor || 'rgba(56, 189, 248, 0.25)',
        stroke: this.props.rippleColor || '#38bdf8',
        'stroke-width': 1.5,
        'stroke-dasharray': '5 3',
        filter: 'drop-shadow(0px 0px 8px rgba(56, 189, 248, 0.75))',
        opacity: 0.85
      });
      this.container.appendChild(this.haloElement);
    }

    // 2. Click Ripple Elements (double concentric pulse rings)
    this.rippleElement = Renderer.createElement('circle');
    Renderer.setAttributes(this.rippleElement, {
      cx: 0,
      cy: 0,
      r: 0,
      fill: 'none',
      stroke: this.props.rippleColor || '#38bdf8',
      'stroke-width': 3,
      opacity: 0
    });
    this.container.appendChild(this.rippleElement);

    this.outerRippleElement = Renderer.createElement('circle');
    Renderer.setAttributes(this.outerRippleElement, {
      cx: 0,
      cy: 0,
      r: 0,
      fill: 'none',
      stroke: this.props.rippleColor || '#38bdf8',
      'stroke-width': 1.5,
      opacity: 0
    });
    this.container.appendChild(this.outerRippleElement);

    // 3. Vector Cursor Shape
    this.cursorElement = Renderer.createElement('g');
    this.renderCursorShape(this.cursorElement, size);
    this.container.appendChild(this.cursorElement);

    // 4. Floating Action / Keystroke HUD Badge
    this.badgeElement = Renderer.createElement('g');
    this.badgeElement.style.display = 'none';

    const badgeBg = Renderer.createElement('rect');
    Renderer.setAttributes(badgeBg, {
      x: 18,
      y: 22,
      rx: 6,
      ry: 6,
      fill: 'rgba(15, 23, 42, 0.92)',
      stroke: this.props.rippleColor || '#38bdf8',
      'stroke-width': 1.2,
      filter: 'drop-shadow(0px 3px 8px rgba(0,0,0,0.6))'
    });
    this.badgeElement.appendChild(badgeBg);

    this.badgeTextElement = Renderer.createElement('text');
    Renderer.setAttributes(this.badgeTextElement, {
      x: 28,
      y: 38,
      fill: '#f8fafc',
      'font-size': 12,
      'font-weight': '700',
      'font-family': 'Inter, system-ui, sans-serif'
    });
    this.badgeElement.appendChild(this.badgeTextElement);
    this.container.appendChild(this.badgeElement);

    this.applyTransform();
  }

  private renderCursorShape(parent: SVGElement, size: number): void {
    const type = this.props.cursorType || 'arrow';
    const fill = this.props.cursorColor || '#ffffff';
    const stroke = this.props.borderColor || '#000000';

    if (type === 'hand') {
      const path = Renderer.createElement('path');
      Renderer.setAttributes(path, {
        d: `M 0 0 C 1 1 2 2 3 4 L 4 10 C 4 11 3 12 2 12 C 1 12 0 11 0 10 L -1 7 L -2 9 C -3 10 -4 10 -5 9 C -6 8 -6 7 -5 6 L -3 3 Z`,
        fill: fill,
        stroke: stroke,
        'stroke-width': 1.6,
        'stroke-linejoin': 'round',
        filter: 'drop-shadow(0px 2px 5px rgba(0,0,0,0.45))',
        transform: `scale(${size / 14}) translate(4, 2)`
      });
      parent.appendChild(path);

    } else if (type === 'ibeam') {
      const path = Renderer.createElement('path');
      Renderer.setAttributes(path, {
        d: `M -5 -10 L 5 -10 M 0 -10 L 0 10 M -5 10 L 5 10`,
        fill: 'none',
        stroke: stroke,
        'stroke-width': 2.5,
        'stroke-linecap': 'round',
        filter: 'drop-shadow(0px 1px 3px rgba(0,0,0,0.5))',
        transform: `scale(${size / 20})`
      });
      parent.appendChild(path);

    } else if (type === 'crosshair') {
      const path = Renderer.createElement('path');
      Renderer.setAttributes(path, {
        d: `M 0 -12 L 0 -4 M 0 4 L 0 12 M -12 0 L -4 0 M 4 0 L 12 0 M 0 0 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0`,
        fill: 'none',
        stroke: stroke,
        'stroke-width': 2,
        transform: `scale(${size / 20})`
      });
      parent.appendChild(path);

    } else {
      // Classic High-Visibility Windows Arrow Cursor
      const path = Renderer.createElement('path');
      Renderer.setAttributes(path, {
        d: `M 0 0 L 0 18 L 5 14 L 9.5 22 L 12.5 20.5 L 8 13 L 13.5 13 Z`,
        fill: fill,
        stroke: stroke,
        'stroke-width': 1.8,
        'stroke-linejoin': 'round',
        filter: 'drop-shadow(0px 3px 6px rgba(0,0,0,0.5))',
        transform: `scale(${size / 20})`
      });
      parent.appendChild(path);
    }
  }

  /**
   * Triggers an animated click ripple with expanding concentric pulses and optional HUD label.
   */
  public triggerClick(rippleColor?: string, actionLabel?: string): void {
    if (!this.rippleElement) return;
    const color = rippleColor || this.props.rippleColor || '#38bdf8';
    let progress = 0;
    const maxRadius = (this.props.size || 38) * 2.0;

    if (actionLabel) {
      this.showActionBadge(actionLabel, 1200);
    }

    const animate = () => {
      progress += 0.045;
      if (progress <= 1) {
        const radius = progress * maxRadius;
        const outerRadius = Math.max(0, (progress - 0.15)) * (maxRadius * 1.3);
        const opacity = (1 - progress) * 0.95;

        Renderer.setAttributes(this.rippleElement, {
          r: radius,
          stroke: color,
          'stroke-width': 3.5 * (1 - progress * 0.4),
          fill: color,
          'fill-opacity': ((1 - progress) * 0.3).toFixed(2),
          opacity: opacity
        });

        if (this.outerRippleElement && progress > 0.15) {
          Renderer.setAttributes(this.outerRippleElement, {
            r: outerRadius,
            stroke: color,
            'stroke-width': 1.8 * (1 - progress),
            opacity: opacity * 0.7
          });
        }

        if (this.cursorElement) {
          const scale = progress < 0.25 ? 0.86 : 1.0;
          this.cursorElement.setAttribute('transform', `scale(${scale})`);
        }
        if (typeof requestAnimationFrame !== 'undefined') {
          requestAnimationFrame(animate);
        }
      } else {
        Renderer.setAttributes(this.rippleElement, { r: 0, opacity: 0, 'fill-opacity': 0 });
        if (this.outerRippleElement) {
          Renderer.setAttributes(this.outerRippleElement, { r: 0, opacity: 0 });
        }
        if (this.cursorElement) {
          this.cursorElement.removeAttribute('transform');
        }
      }
    };

    animate();
  }

  /**
   * Displays an interactive Action / Keystroke HUD badge floating near the pointer.
   */
  public showActionBadge(label: string, durationMs: number = 1800): void {
    if (!this.badgeElement || !this.badgeTextElement) return;

    this.badgeTextElement.textContent = label;
    const approxWidth = Math.max(80, label.length * 7.5 + 24);
    const rect = this.badgeElement.querySelector('rect');
    if (rect) {
      rect.setAttribute('width', String(approxWidth));
      rect.setAttribute('height', '26');
    }

    this.badgeElement.style.display = 'block';

    if ((this as any)._badgeTimer) {
      clearTimeout((this as any)._badgeTimer);
    }

    (this as any)._badgeTimer = setTimeout(() => {
      if (this.badgeElement) {
        this.badgeElement.style.display = 'none';
      }
    }, durationMs);
  }

  public setCursorType(type: CursorType): void {
    if (this.props.cursorType !== type) {
      this.props.cursorType = type;
      this.render();
    }
  }
}
